"use strict";
/**
 * Shared test constants for interest rate serialization tests.
 *
 * These constants define the expected serialization format for interest rate types
 * to ensure TypeScript and Python implementations serialize to identical JSON strings.
 *
 * IMPORTANT: After updating this file, run `pnpm translate:python` in aven_types
 * to regenerate the Python version at common/avenTypes/interestRateTest.py
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.INTEREST_RATE_SERIALIZATION_TEST_CASES = void 0;
const interestRate_1 = require("./interestRate");
// @TranslateToPython
/**
 * Test cases for interest rate serialization.
 * Each case specifies the construction parameters and expected JSON output.
 *
 * The serialized format includes:
 * - aprAsDecimal: The APR value as a decimal number
 * - is<TypeName>: A boolean discriminator field set to true
 *
 * Note: precisionInDecimal is intentionally excluded from serialization.
 */
exports.INTEREST_RATE_SERIALIZATION_TEST_CASES = [
    // MortgageProductPoints (precision 5, can be negative)
    {
        description: 'MortgageProductPoints from decimal 0.0125 (1.25 points)',
        interestRateType: 'MortgageProductPoints',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0.0125,
        expectedSerializedJson: '{"aprAsDecimal":0.0125,"isMortgageProductPoints":true}',
    },
    {
        description: 'MortgageProductPoints from percent 1.25 (1.25 points)',
        interestRateType: 'MortgageProductPoints',
        constructionUnits: interestRate_1.InterestRateUnits.percent,
        aprValue: 1.25,
        expectedSerializedJson: '{"aprAsDecimal":0.0125,"isMortgageProductPoints":true}',
    },
    {
        description: 'MortgageProductPoints negative (lender credit)',
        interestRateType: 'MortgageProductPoints',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: -0.005,
        expectedSerializedJson: '{"aprAsDecimal":-0.005,"isMortgageProductPoints":true}',
    },
    {
        description: 'MortgageProductPoints zero',
        interestRateType: 'MortgageProductPoints',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0,
        expectedSerializedJson: '{"aprAsDecimal":0,"isMortgageProductPoints":true}',
    },
    // MortgageProductInterestRate (precision 5, must be non-negative)
    {
        description: 'MortgageProductInterestRate from decimal 0.06875 (6.875%)',
        interestRateType: 'MortgageProductInterestRate',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0.06875,
        expectedSerializedJson: '{"aprAsDecimal":0.06875,"isMortgageProductInterestRate":true}',
    },
    {
        description: 'MortgageProductInterestRate from percent 6.875',
        interestRateType: 'MortgageProductInterestRate',
        constructionUnits: interestRate_1.InterestRateUnits.percent,
        aprValue: 6.875,
        expectedSerializedJson: '{"aprAsDecimal":0.06875,"isMortgageProductInterestRate":true}',
    },
    {
        description: 'MortgageProductInterestRate zero',
        interestRateType: 'MortgageProductInterestRate',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0,
        expectedSerializedJson: '{"aprAsDecimal":0,"isMortgageProductInterestRate":true}',
    },
    // MortgageProductApr (precision 5, must be non-negative)
    {
        description: 'MortgageProductApr from decimal 0.07125 (7.125%)',
        interestRateType: 'MortgageProductApr',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0.07125,
        expectedSerializedJson: '{"aprAsDecimal":0.07125,"isMortgageProductApr":true}',
    },
    {
        description: 'MortgageProductApr from percent 7.125',
        interestRateType: 'MortgageProductApr',
        constructionUnits: interestRate_1.InterestRateUnits.percent,
        aprValue: 7.125,
        expectedSerializedJson: '{"aprAsDecimal":0.07125,"isMortgageProductApr":true}',
    },
    // InterestRateMarginForAven (precision 4)
    {
        description: 'InterestRateMarginForAven from decimal 0.0374 (3.74%)',
        interestRateType: 'InterestRateMarginForAven',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0.0374,
        expectedSerializedJson: '{"aprAsDecimal":0.0374,"isInterestRateMarginForAven":true}',
    },
    {
        description: 'InterestRateMarginForAven from percent 3.74',
        interestRateType: 'InterestRateMarginForAven',
        constructionUnits: interestRate_1.InterestRateUnits.percent,
        aprValue: 3.74,
        expectedSerializedJson: '{"aprAsDecimal":0.0374,"isInterestRateMarginForAven":true}',
    },
    // InterestRateIndexForAven (precision 4)
    {
        description: 'InterestRateIndexForAven from decimal 0.0725 (7.25%)',
        interestRateType: 'InterestRateIndexForAven',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0.0725,
        expectedSerializedJson: '{"aprAsDecimal":0.0725,"isInterestRateIndexForAven":true}',
    },
    {
        description: 'InterestRateIndexForAven from percent 7.25',
        interestRateType: 'InterestRateIndexForAven',
        constructionUnits: interestRate_1.InterestRateUnits.percent,
        aprValue: 7.25,
        expectedSerializedJson: '{"aprAsDecimal":0.0725,"isInterestRateIndexForAven":true}',
    },
    // SimpleFixedInterestRate (precision 4). The discriminator stays isSipInterestRate for backcompat.
    {
        description: 'SimpleFixedInterestRate from decimal 0.0624 (6.24%)',
        interestRateType: 'SimpleFixedInterestRate',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0.0624,
        expectedSerializedJson: '{"aprAsDecimal":0.0624,"isSipInterestRate":true}',
    },
    {
        description: 'SimpleFixedInterestRate from percent 6.24',
        interestRateType: 'SimpleFixedInterestRate',
        constructionUnits: interestRate_1.InterestRateUnits.percent,
        aprValue: 6.24,
        expectedSerializedJson: '{"aprAsDecimal":0.0624,"isSipInterestRate":true}',
    },
    {
        description: 'SimpleFixedInterestRate zero',
        interestRateType: 'SimpleFixedInterestRate',
        constructionUnits: interestRate_1.InterestRateUnits.decimal,
        aprValue: 0,
        expectedSerializedJson: '{"aprAsDecimal":0,"isSipInterestRate":true}',
    },
];
